import React, { useEffect, useMemo, useState } from 'react';
import api from './api';

const EMPTY_FORM = { name: '', description: '', entryFee: '50', maxPlayers: '', pairingMode: 'shuffle' };
const SECTIONS = { overview: 'overview', players: 'players', matches: 'matches', subMatches: 'subMatches' };
const PAGE_SIZE = 10;

function roundsNeeded(players) {
  let left = Math.max(1, Number(players || 0));
  let rounds = 0;
  while (left > 1) {
    left = Math.ceil(left / 2);
    rounds += 1;
  }
  return rounds;
}

function formatRankRange(startRank, endRank) {
  if (!startRank || !endRank) return 'placement ranks';
  return Number(startRank) === Number(endRank) ? `rank ${startRank}` : `ranks ${startRank}-${endRank}`;
}

function buildVirtualRoundOne(participants = []) {
  return {
    bracketType: 'main',
    roundNumber: 1,
    label: 'Round 1',
    status: 'upcoming',
    participantIds: participants.map((participant) => String(participant.id || participant._id || '')).filter(Boolean),
    byeParticipantIds: [],
    matches: [],
  };
}

function badge(status) {
  const s = String(status || '').toLowerCase();
  if (['won'].includes(s)) return { ...styles.badge, ...styles.badgeWon };
  if (['bye', 'byes'].includes(s)) return { ...styles.badge, ...styles.badgeBye };
  if (['live', 'active'].includes(s)) return { ...styles.badge, ...styles.badgeLive };
  if (['completed', 'champion', 'ranked'].includes(s)) return { ...styles.badge, ...styles.badgeDone };
  if (['eliminated', 'cancelled'].includes(s)) return { ...styles.badge, ...styles.badgeDanger };
  if (['not started', 'not_started', 'upcoming'].includes(s)) return { ...styles.badge, ...styles.badgeNotStarted };
  return { ...styles.badge, ...styles.badgeDefault };
}

function playerFilterChipStyle(filterId, isActive) {
  const palette = {
    all: {
      background: '#fff8f1',
      color: '#7b563d',
      borderColor: 'rgba(184,105,67,0.24)',
    },
    eliminated: {
      background: '#fff1f0',
      color: '#a14b4b',
      borderColor: 'rgba(199,95,95,0.28)',
    },
    won: {
      background: '#eef8f0',
      color: '#2f6a42',
      borderColor: 'rgba(98,161,115,0.28)',
    },
    byes: {
      background: '#eef6ff',
      color: '#3d629f',
      borderColor: 'rgba(79,120,199,0.28)',
    },
    not_started: {
      background: '#fff8d9',
      color: '#8a6a12',
      borderColor: 'rgba(194,155,49,0.32)',
    },
  };
  const tone = palette[filterId] || palette.all;
  return {
    ...styles.filterChip,
    ...tone,
    ...(isActive ? styles.filterChipActive : {}),
  };
}

function paginate(items, page) {
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  return { safePage, totalPages, items: items.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE) };
}

function roundParticipantIds(round) {
  if (Array.isArray(round?.participantIds) && round.participantIds.length > 0) {
    return round.participantIds.map(String);
  }
  return Array.from(new Set(
    (round?.matches || [])
      .flatMap((match) => [match.player1ParticipantId, match.player2ParticipantId])
      .filter(Boolean)
      .map(String)
  ));
}

function getDisplayRoundStatus(round, activeRoundNumber = 0) {
  if (!round) return 'upcoming';
  const participantCount = roundParticipantIds(round).length;
  const winnerCount = new Set(
    (round.matches || [])
      .map((match) => match.winnerParticipantId)
      .filter(Boolean)
      .map(String)
  ).size;
  const byeCount = Array.isArray(round?.byeParticipantIds) ? round.byeParticipantIds.length : 0;
  const resolvedCount = Math.min(participantCount, winnerCount + byeCount);
  if (participantCount > 0 && resolvedCount >= Math.ceil(participantCount / 2) && resolvedCount >= participantCount - Math.floor(participantCount / 2)) {
    return 'completed';
  }
  if (Number(round?.roundNumber || 0) === Number(activeRoundNumber || 0)) return 'live';
  return 'upcoming';
}

function getParticipantRoundStatus(participant, selectedRound, winnerIds, loserIds, byeIds = []) {
  const participantId = String(participant?.id || '');
  if (byeIds.includes(participantId)) return 'Bye';
  if (loserIds.has(participantId)) return 'Eliminated';
  if (winnerIds.has(participantId)) return 'Won';
  return 'Not started';
}

function formatPlayedAt(value) {
  if (!value) return 'Not started';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not started';
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatScoreline(match) {
  return `${Number(match?.score1 || 0)}-${Number(match?.score2 || 0)}`;
}

function getParticipantRoundMatch(selectedRound, participantId) {
  const id = String(participantId || '');
  if (!id || !selectedRound) return null;
  return (selectedRound.matches || []).find((match) =>
    String(match.player1ParticipantId || '') === id || String(match.player2ParticipantId || '') === id
  ) || null;
}

function getParticipantRoundScoreline(participant, selectedRound, byeIds = []) {
  const participantId = String(participant?.id || '');
  if (byeIds.includes(participantId)) return 'BYE';
  const match = getParticipantRoundMatch(selectedRound, participantId);
  if (!match) return 'Not started';
  const isPlayerOne = String(match.player1ParticipantId || '') === participantId;
  return isPlayerOne ? `${match.score1}-${match.score2}` : `${match.score2}-${match.score1}`;
}

function Metric({ label, value }) {
  return (
    <div style={styles.metric}>
      <div style={styles.metricValue}>{value}</div>
      <div style={styles.metricLabel}>{label}</div>
    </div>
  );
}

function Pager({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div style={styles.pager}>
      <button type="button" onClick={() => onChange(Math.max(1, page - 1))} style={styles.subtleBtn}>Previous</button>
      <div style={styles.pagerText}>Page {page}/{totalPages}</div>
      <button type="button" onClick={() => onChange(Math.min(totalPages, page + 1))} style={styles.subtleBtn}>Next</button>
    </div>
  );
}

function RefreshButton({ onClick, spinning = false, label = 'Refresh' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={spinning}
      style={{ ...styles.refreshBtn, ...(spinning ? styles.refreshBtnSpinning : {}) }}
      title={label}
      aria-label={label}
    >
      <RefreshIcon spinning={spinning} />
    </button>
  );
}

function RefreshIcon({ spinning = false }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <g>
        <path
          d="M18 8.4A7 7 0 1 0 19 12"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14.8 5.6H18V8.8"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {spinning && (
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            from="0 12 12"
            to="360 12 12"
            dur="0.85s"
            repeatCount="indefinite"
          />
        )}
      </g>
    </svg>
  );
}

function TournamentDetailLoader() {
  return (
    <div style={styles.detailLoaderWrap}>
      <div style={styles.detailLoaderCard}>
        <div style={styles.detailLoaderSpinner} aria-hidden="true">
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
            <circle cx="15" cy="15" r="10" stroke="rgba(180,102,66,0.16)" strokeWidth="3" />
            <path d="M15 5a10 10 0 0 1 10 10" stroke="#b46642" strokeWidth="3" strokeLinecap="round">
              <animateTransform
                attributeName="transform"
                attributeType="XML"
                type="rotate"
                from="0 15 15"
                to="360 15 15"
                dur="0.75s"
                repeatCount="indefinite"
              />
            </path>
          </svg>
        </div>
        <div style={styles.panelTitle}>Loading tournament details</div>
        <div style={styles.panelMeta}>Fetching players, rounds, matches, and prize data for this tournament.</div>
      </div>
    </div>
  );
}

export default function TournamentAdminPanel({ initialTournamentId = '', detailOnly = false, routeInfo = null, onOpenTournamentDetails, onNavigate, onBack, readOnly = false }) {
  const isReadOnly = Boolean(readOnly);
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editorMode, setEditorMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshingSection, setRefreshingSection] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [playerSearch, setPlayerSearch] = useState('');
  const [playerFilter, setPlayerFilter] = useState('all');
  const [playerPage, setPlayerPage] = useState(1);
  const [matchPage, setMatchPage] = useState(1);
  const [subMatchPage, setSubMatchPage] = useState(1);
  const [showPrizeBoard, setShowPrizeBoard] = useState(false);

  async function loadTournaments(preferredId = '', options = {}) {
    const { openDetails = false, silent = false, section = '' } = options;
    if (!silent) setLoading(true);
    if (silent && section) setRefreshingSection(section);
    setError('');
    try {
      const listEndpoint = isReadOnly ? '/matches/tournaments' : '/admin/tournaments';
      const detailEndpointPrefix = isReadOnly ? '/matches/tournaments' : '/admin/tournaments';
      const res = await api.get(listEndpoint);
      const list = res.data?.data?.tournaments || [];
      setTournaments(list);
      const nextId = preferredId || (openDetails ? selectedTournamentId : '') || '';
      setSelectedTournamentId(nextId);
      if (nextId && openDetails) {
        const detailRes = await api.get(`${detailEndpointPrefix}/${nextId}`);
        const tournament = detailRes.data?.data?.tournament || null;
        setSelectedTournament(tournament);
        if (!isReadOnly) {
          setForm({
            name: tournament?.name || '',
            description: tournament?.description || '',
            entryFee: String(tournament?.entryFee ?? 0),
            maxPlayers: String(tournament?.maxPlayers ?? 50),
            pairingMode: 'shuffle',
          });
        }
      } else {
        setSelectedTournament(null);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load tournaments');
      if (!silent) {
        setTournaments([]);
        setSelectedTournament(null);
      }
    } finally {
      if (!silent) setLoading(false);
      if (silent && section) setRefreshingSection((current) => (current === section ? '' : current));
    }
  }

  useEffect(() => {
    if (detailOnly && initialTournamentId) {
      loadTournaments(initialTournamentId, { openDetails: true });
      return;
    }
    loadTournaments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailOnly, initialTournamentId]);

  useEffect(() => { setPlayerPage(1); }, [playerSearch, playerFilter, selectedTournamentId, routeInfo?.roundNumber]);
  useEffect(() => { setSubMatchPage(1); }, [selectedTournamentId, routeInfo?.roundNumber]);

  function refreshCurrentView(section = 'detail') {
    if (!selectedTournamentId) return;
    loadTournaments(selectedTournamentId, { openDetails: true, silent: true, section }).catch(() => {});
  }

  async function handleStartTournament() {
    if (!selectedTournamentId) return;
    if (isReadOnly) {
      setError('This account can only view tournaments.');
      return;
    }
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const res = await api.post(`/admin/tournaments/${selectedTournamentId}/start`);
      await loadTournaments(selectedTournamentId, { openDetails: true });
      setMessage(res.data?.message || 'Round 1 started successfully.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to start round 1');
    } finally {
      setSaving(false);
    }
  }

  async function handleStartRound(roundToStart) {
    if (!selectedTournamentId || !roundToStart) return;
    if (isReadOnly) {
      setError('This account can only view tournaments.');
      return;
    }
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const res = await api.post(`/admin/tournaments/${selectedTournamentId}/rounds/${roundToStart}/start`);
      await loadTournaments(selectedTournamentId, { openDetails: true });
      setMessage(res.data?.message || `Round ${roundToStart} started successfully.`);
    } catch (err) {
      setError(err?.response?.data?.message || `Failed to start round ${roundToStart}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleReleasePrizes() {
    if (!selectedTournamentId) return;
    if (isReadOnly) {
      setError('This account can only view tournaments.');
      return;
    }
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const res = await api.post(`/admin/tournaments/${selectedTournamentId}/release-prizes`);
      await loadTournaments(selectedTournamentId, { openDetails: true });
      setMessage(res.data?.message || 'Tournament prizes released successfully.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to release tournament prizes');
    } finally {
      setSaving(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (isReadOnly) {
      setError('This account can only view tournaments.');
      return;
    }
    const maxPlayers = Number(form.maxPlayers || 0);
    if (!Number.isFinite(maxPlayers) || maxPlayers < 20 || maxPlayers > 50) {
      setError('Max players must be between 20 and 50');
      return;
    }
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const res = await api.post('/admin/tournaments', { name: form.name, description: form.description, entryFee: Number(form.entryFee || 0), maxPlayers, pairingMode: 'shuffle' });
      setEditorMode(null);
      setForm(EMPTY_FORM);
      await loadTournaments(res.data?.data?.tournament?.id || '', { openDetails: true });
      setMessage(res.data?.message || 'Tournament created successfully.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create tournament');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveTournament(e) {
    e.preventDefault();
    if (!selectedTournamentId) return;
    if (isReadOnly) {
      setError('This account can only view tournaments.');
      return;
    }
    const maxPlayers = Number(form.maxPlayers || 0);
    if (!Number.isFinite(maxPlayers) || maxPlayers < 20 || maxPlayers > 50) {
      setError('Max players must be between 20 and 50');
      return;
    }
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const res = await api.patch(`/admin/tournaments/${selectedTournamentId}`, { name: form.name, description: form.description, entryFee: Number(form.entryFee || 0), maxPlayers, pairingMode: 'shuffle' });
      setEditorMode(null);
      await loadTournaments(selectedTournamentId, { openDetails: true });
      setMessage(res.data?.message || 'Tournament updated successfully.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update tournament');
    } finally {
      setSaving(false);
    }
  }

  const stats = useMemo(() => ({
    total: tournaments.length,
    active: tournaments.filter((item) => item.status === 'active').length,
    joined: tournaments.reduce((sum, item) => sum + Number(item.participantCount || 0), 0),
  }), [tournaments]);

  const section = routeInfo?.section || SECTIONS.overview;
  const currentSection = Object.values(SECTIONS).includes(section) ? section : SECTIONS.overview;
  const roundNumber = Number(routeInfo?.roundNumber || 0);
  const rounds = selectedTournament?.rounds || [];
  const participants = selectedTournament?.participants || [];
  const mainRoundsAsc = [...rounds].filter((round) => round.bracketType === 'main').sort((a, b) => Number(a.roundNumber || 0) - Number(b.roundNumber || 0));
  const mainRounds = [...mainRoundsAsc].slice().sort((a, b) => Number(b.roundNumber || 0) - Number(a.roundNumber || 0));
  const fallbackRound = mainRoundsAsc[0] || (participants.length > 0 ? buildVirtualRoundOne(participants) : null);
  const selectedRound = mainRoundsAsc.find((round) => Number(round.roundNumber || 0) === roundNumber)
    || mainRoundsAsc.find((round) => Number(round.roundNumber || 0) === Number(selectedTournament?.activeRoundNumber || 0))
    || (Number(roundNumber || 0) === 1 ? fallbackRound : null)
    || fallbackRound;
  const activeRoundNumber = Number(selectedTournament?.activeRoundNumber || 0) || Number(mainRounds[0]?.roundNumber || 0);
  const activeRound = mainRounds.find((round) => Number(round.roundNumber || 0) === activeRoundNumber) || null;
  const selectedRoundDisplayStatus = getDisplayRoundStatus(selectedRound, activeRoundNumber);
  const activeRoundDisplayStatus = getDisplayRoundStatus(activeRound, activeRoundNumber);
  const upcomingRound = mainRounds
    .slice()
    .sort((a, b) => Number(a.roundNumber || 0) - Number(b.roundNumber || 0))
    .find((round) => getDisplayRoundStatus(round, activeRoundNumber) === 'upcoming') || null;
  const requiredPlayers = Number(selectedTournament?.maxPlayers || 0);
  const allPlayersRegistered = Number(selectedTournament?.participantCount || 0) === requiredPlayers && requiredPlayers > 0;
  const readyForRoundOne = allPlayersRegistered && Number(mainRounds.length || 0) === 0 && ['draft', 'upcoming'].includes(String(selectedTournament?.status || '').toLowerCase());
  const overviewHeadline = readyForRoundOne
    ? `${requiredPlayers} players registered. Start round 1.`
    : upcomingRound
      ? `Round ${upcomingRound.roundNumber} is ready to start.`
      : activeRound
        ? `Round ${activeRound.roundNumber} is live.`
        : `${selectedTournament?.participantCount || 0}/${requiredPlayers || '-'} players registered.`;
  const overviewSubline = readyForRoundOne
    ? 'Admin can now open round 1. Players will see the live tournament state in their panel.'
    : upcomingRound
      ? `Winners from round ${Math.max(1, Number(upcomingRound.roundNumber || 1) - 1)} are already placed in the next round tab.`
      : `Tournament will unlock once exactly ${requiredPlayers || '-'} players are registered.`;
  const progressRound = selectedRound || upcomingRound || activeRound || fallbackRound || null;
  const progressRoundNumber = Number(progressRound?.roundNumber || (participants.length > 0 ? 1 : 0));
  const highlightedRoundNumber = Number(selectedRound?.roundNumber || progressRound?.roundNumber || (participants.length > 0 ? 1 : 0));
  const totalRounds = Math.max(1, roundsNeeded(selectedTournament?.maxPlayers || 50));
  const progressPercent = Math.max(0, Math.min(100, totalRounds <= 1 ? 0 : ((Math.max(1, progressRoundNumber) - 1) / (totalRounds - 1)) * 100));
  const selectedRoundMatchList = (selectedRound?.matches || []);
  const completedSelectedRoundMatches = selectedRoundMatchList.filter((match) => String(match.status || '').toLowerCase() === 'completed');
  const selectedRoundByeIds = Array.isArray(selectedRound?.byeParticipantIds) ? selectedRound.byeParticipantIds.map(String) : [];
  const selectedRoundWinnerIds = new Set([
    ...completedSelectedRoundMatches.map((match) => String(match.winnerParticipantId || '')).filter(Boolean),
    ...selectedRoundByeIds,
  ]);
  const selectedRoundLoserIds = new Set(
    completedSelectedRoundMatches.map((match) => String(match.loserParticipantId || '')).filter(Boolean)
  );
  const selectedRoundParticipantIdSet = new Set(roundParticipantIds(selectedRound));
  const selectedRoundPlayers = participants.filter((participant) => selectedRoundParticipantIdSet.has(String(participant.id || '')));
  const selectedRoundPlayerCount = roundParticipantIds(selectedRound).length;
  const selectedRoundCompletedCount = completedSelectedRoundMatches.length;
  const requiredPrizeRanks = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const resolvedPrizeRanks = new Set(
    participants
      .map((participant) => Number(participant.finalRank || 0))
      .filter(Boolean)
  );
  const participantByFinalRank = new Map(
    participants
      .filter((participant) => Number(participant.finalRank || 0) > 0)
      .map((participant) => [Number(participant.finalRank || 0), participant])
  );
  const tournamentPrizeBoard = requiredPrizeRanks.map((rank) => {
    const participant = participantByFinalRank.get(rank) || null;
    const prize = (selectedTournament?.prizes || []).find((entry) => Number(entry.rank || 0) === rank) || null;
    return {
      rank,
      coins: Number(prize?.coins || 0),
      participant,
    };
  });
  const hasAllPrizeRanks = requiredPrizeRanks.every((rank) => resolvedPrizeRanks.has(rank));
  const prizeReleaseDone = Boolean(selectedTournament?.prizesAwardedAt && selectedTournament?.platformSettledAt);
  const canReleasePrizes = !isReadOnly && hasAllPrizeRanks && !prizeReleaseDone;
  const resolvedSelectedRoundMatches = completedSelectedRoundMatches.filter((match) => !match.isBye || match.winnerParticipantId || match.player2Name === 'BYE');
  const selectedRoundCompletedMatchesPage = paginate(
    resolvedSelectedRoundMatches,
    matchPage
  );
  const filteredPlayers = selectedRoundPlayers.filter((participant) => {
    const q = playerSearch.trim().toLowerCase();
    const participantId = String(participant.id || '');
    const matchesSearch = !q || [participant.name, participant.username, participant.parentAgentName, participant.parentAgentUsername].filter(Boolean).join(' ').toLowerCase().includes(q);
    if (!matchesSearch) return false;
    if (playerFilter === 'eliminated') return selectedRoundLoserIds.has(participantId);
    if (playerFilter === 'won') return selectedRoundWinnerIds.has(participantId) && !selectedRoundByeIds.includes(participantId);
    if (playerFilter === 'byes') return selectedRoundByeIds.includes(participantId);
    if (playerFilter === 'not_started') return !selectedRoundLoserIds.has(participantId) && !selectedRoundWinnerIds.has(participantId) && !selectedRoundByeIds.includes(participantId);
    return true;
  });
  const playersPage = paginate(filteredPlayers, playerPage);
  const placementRounds = [...rounds]
    .filter((round) => String(round.bracketType || '') !== 'main')
    .filter((round) => {
      const sourceMainRoundNumber = Number(round.sourceMainRoundNumber || 0);
      if (sourceMainRoundNumber > 0) {
        return sourceMainRoundNumber === Number(selectedRound?.roundNumber || 0);
      }
      const selectedRoundNumber = Number(selectedRound?.roundNumber || 0);
      if (selectedRoundNumber === 3) return String(round.bracketType || '') === 'placement-8-9';
      if (selectedRoundNumber === 4) return String(round.bracketType || '') === 'placement-5-7';
      if (selectedRoundNumber === 5) return String(round.bracketType || '') === 'placement-3-4';
      return false;
    })
    .sort((a, b) => {
      if (Number(a.roundNumber || 0) !== Number(b.roundNumber || 0)) {
        return Number(a.roundNumber || 0) - Number(b.roundNumber || 0);
      }
      return Number(a.stageOrder || 0) - Number(b.stageOrder || 0);
    });
  const placementParticipants = Array.from(new Set(
    placementRounds.flatMap((round) => roundParticipantIds(round))
  ))
    .map((participantId) => participants.find((participant) => String(participant.id || '') === String(participantId)))
    .filter(Boolean);
  const placementRoundsPage = paginate(
    placementRounds.map((round) => ({
      ...round,
      resolvedMatches: (round.matches || []).filter((match) => !match.isBye || match.winnerParticipantId || match.player2Name === 'BYE'),
    })).filter((round) => round.resolvedMatches.length > 0),
    subMatchPage
  );
  const showSubMatchesTab = placementRounds.length > 0 || currentSection === SECTIONS.subMatches;
  const placementCurrentRound = placementRounds.find((round) => String(round.status || '').toLowerCase() === 'live')
    || placementRounds.find((round) => String(round.status || '').toLowerCase() === 'upcoming')
    || placementRounds[placementRounds.length - 1]
    || null;
  const placementRankStart = Number(placementRounds[0]?.placementStartRank || 0) || null;
  const placementRankEnd = Number(placementRounds[0]?.placementEndRank || 0) || null;
  const subMatchDescription = placementRounds.length > 0
    ? `Round ${selectedRound?.roundNumber || '-'} loser bracket for ${formatRankRange(placementRankStart, placementRankEnd)}.`
    : 'Sub matches for placement ranks.';
  const nextStartRoundNumber = readyForRoundOne
    ? 1
    : Number(upcomingRound?.roundNumber || 0);
  const hasLiveMainRound = Boolean(activeRound && activeRoundDisplayStatus === 'live');
  const canShowStartGameButton = !isReadOnly && (readyForRoundOne || Boolean(
    upcomingRound
    && !hasLiveMainRound
    && nextStartRoundNumber > 0
    && nextStartRoundNumber <= totalRounds
  ));
  const showRoundLiveNotice = hasLiveMainRound && !canShowStartGameButton;
  const startGameLabel = nextStartRoundNumber > 0
    ? `Start Game (Round ${nextStartRoundNumber})`
    : 'Start Game';

  useEffect(() => {
    if (selectedRoundDisplayStatus !== 'completed' && /already completed/i.test(String(error || ''))) {
      setError('');
    }
  }, [error, selectedRoundDisplayStatus]);

  async function handleStartGame() {
    if (readyForRoundOne) {
      await handleStartTournament();
      return;
    }
    if (nextStartRoundNumber > 0) {
      await handleStartRound(nextStartRoundNumber);
    }
  }

  function goto(sectionName, extra = {}) {
    setError('');
    setMessage('');
    if (typeof onNavigate === 'function') {
      onNavigate({ view: 'tournamentDetails', tournamentId: selectedTournamentId, section: sectionName, roundNumber: extra.roundNumber || '' });
    }
  }

  const canEdit = !isReadOnly
    && Boolean(selectedTournament)
    && (!['active', 'completed'].includes(String(selectedTournament?.status || '').toLowerCase()) || Number(selectedTournament?.rounds?.length || 0) > 0);

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <div>
          {/* <div style={styles.kicker}>Tournament Control</div> */}
          {!detailOnly && <div style={styles.topTitle}>Compact tournament admin workspace</div>}
        </div>
        {!selectedTournament && !detailOnly && (
          <div style={styles.topStats}>
            <Metric label="Tournaments" value={stats.total} />
            <Metric label="Active" value={stats.active} />
            <Metric label="Entries" value={stats.joined} />
          </div>
        )}
      </div>

      {(message || error) && <div style={{ ...styles.notice, ...(error ? styles.noticeError : styles.noticeOk) }}>{error || message}</div>}

      {!selectedTournament && !detailOnly && (
        <section style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <div style={styles.panelTitle}>Tournament List</div>
              <div style={styles.panelMeta}>Each row opens its own route page.</div>
            </div>
            <div style={styles.headerActions}>
              <RefreshButton
                onClick={() => loadTournaments('', { section: 'list' })}
                spinning={loading || refreshingSection === 'list'}
                label="Refresh tournaments"
              />
              {!isReadOnly && (
                <button type="button" onClick={() => setEditorMode('create')} style={styles.primaryBtn}>Create Tournament</button>
              )}
            </div>
          </div>
          {loading && <div style={styles.empty}>Loading tournaments...</div>}
          {!loading && tournaments.map((item) => (
            <button key={item.id} type="button" onClick={() => onOpenTournamentDetails?.(item.id)} style={styles.listRow}>
              <div style={{ minWidth: 0 }}>
                <div style={styles.listTitle}>{item.name}</div>
                <div style={styles.listMeta}>{item.id} | {item.participantCount}/{item.maxPlayers} players | fee {item.entryFee}</div>
              </div>
              <div style={badge(item.status)}>{item.status}</div>
            </button>
          ))}
        </section>
      )}

      {!selectedTournament && detailOnly && loading && <TournamentDetailLoader />}

      {selectedTournament && (
        <div style={styles.detailPage}>
          <div style={styles.detailHeader}>
            <div style={styles.detailHeaderTop}>
              <div>
                <div style={styles.kicker}>Tournament Control</div>
                <div style={styles.detailHeaderMain}>
                  <span style={styles.detailName}>{selectedTournament.name}</span>
                  <span style={styles.detailPipe}>|</span>
                  <span style={styles.detailMetaInline}>ID: {selectedTournament.id}</span>
                  <span style={styles.detailPipe}>|</span>
                  <span style={styles.detailMetaInline}>Max: {selectedTournament.maxPlayers}</span>
                  <span style={styles.detailPipe}>|</span>
                  <span style={styles.detailMetaInline}>Registered: {selectedTournament.participantCount}</span>
                  <span style={styles.detailPipe}>|</span>
                  <span style={styles.detailMetaInline}>Round: {activeRoundNumber || '-'}</span>
                  <span style={styles.detailPipe}>|</span>
                  <span style={styles.detailMetaInline}>Fee: {selectedTournament.entryFee}</span>
                  <span style={styles.detailPipe}>|</span>
                  <span style={styles.detailMetaInline}>Collected: {Number(selectedTournament.prizePool || 0) + Number(selectedTournament.platformPool || 0)}</span>
                  <span style={styles.detailPipe}>|</span>
                  <span style={styles.detailMetaInline}>Platform Fee: {selectedTournament.platformPool}</span>
                  <span style={styles.detailPipe}>|</span>
                  <span style={styles.detailMetaInline}>Total Rounds: {roundsNeeded(selectedTournament.maxPlayers)}</span>
                </div>
              </div>
              <button type="button" onClick={() => onBack?.()} style={styles.subtleBtn}>Back</button>
            </div>
            <div style={styles.actions}>
              {canEdit && <button type="button" onClick={() => setEditorMode('edit')} style={styles.subtleBtn}>Edit</button>}
            </div>
          </div>

          <div style={styles.detailStack}>
            <div style={styles.progressPanel}>
              <div style={styles.panelHeader}>
                <div>
                  <div style={styles.panelTitle}>Tournament Progress</div>
                  <div style={styles.panelMeta}>Round {progressRoundNumber || '-'} of {totalRounds}</div>
                </div>
                <div style={styles.progressMetaRow}>
                  <RefreshButton
                    onClick={() => refreshCurrentView('progress')}
                    spinning={refreshingSection === 'progress'}
                    label="Refresh tournament progress"
                  />
                  <span style={styles.progressMetaPill}>{selectedTournament.matchTypeLabel}</span>
                  <span style={styles.progressMetaPill}>{selectedTournament.participantCount}/{selectedTournament.maxPlayers} players</span>
                  <button type="button" onClick={() => setShowPrizeBoard(true)} style={styles.subtleBtn}>View Leaderboard</button>
                  {prizeReleaseDone && (
                    <div style={{ ...styles.statusTextPill, ...styles.statusTextPillDone }}>
                      Prize Released
                    </div>
                  )}
                  {canReleasePrizes && (
                    <button type="button" onClick={handleReleasePrizes} disabled={saving} style={styles.primaryBtn}>
                      {saving ? 'Releasing...' : 'Release Prize'}
                    </button>
                  )}
                </div>
              </div>
              <div style={styles.progressTrack}>
                <div style={{ ...styles.progressFill, width: `${progressPercent}%` }} />
              </div>
              <div style={styles.progressStepRow}>
                {Array.from({ length: totalRounds }, (_, index) => {
                  const stepRound = index + 1;
                  const isCurrent = stepRound === highlightedRoundNumber;
                  const isDone = stepRound < progressRoundNumber || String(selectedTournament.status || '').toLowerCase() === 'completed';
                  const hasRoundData = mainRoundsAsc.some((round) => Number(round.roundNumber || 0) === stepRound);
                  return (
                    <button
                      key={stepRound}
                      type="button"
                      onClick={() => goto(currentSection, { roundNumber: stepRound })}
                      style={{
                        ...styles.progressStep,
                        ...(isDone ? styles.progressStepDone : {}),
                        ...(isCurrent ? styles.progressStepCurrent : {}),
                        ...(!hasRoundData && stepRound > Number(upcomingRound?.roundNumber || activeRoundNumber || 0) ? styles.progressStepFuture : {}),
                      }}
                    >
                      <span style={styles.progressStepNumber}>{stepRound}</span>
                      <span style={styles.progressStepLabel}>Round {stepRound}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={styles.topTabs}>
              <button type="button" onClick={() => goto(SECTIONS.overview, { roundNumber: selectedRound?.roundNumber || '' })} style={{ ...styles.topTabBtn, ...(currentSection === SECTIONS.overview ? styles.topTabBtnActive : {}) }}>Overview</button>
              <button type="button" onClick={() => goto(SECTIONS.matches, { roundNumber: selectedRound?.roundNumber || '' })} style={{ ...styles.topTabBtn, ...(currentSection === SECTIONS.matches ? styles.topTabBtnActive : {}) }}>Matches</button>
              <button type="button" onClick={() => goto(SECTIONS.players, { roundNumber: selectedRound?.roundNumber || '' })} style={{ ...styles.topTabBtn, ...(currentSection === SECTIONS.players ? styles.topTabBtnActive : {}) }}>Players</button>
              {showSubMatchesTab && (
                <button type="button" onClick={() => goto(SECTIONS.subMatches, { roundNumber: selectedRound?.roundNumber || '' })} style={{ ...styles.topTabBtn, ...(currentSection === SECTIONS.subMatches ? styles.topTabBtnActive : {}) }}>Sub Matches</button>
              )}
            </div>

            <section style={styles.contentWide}>
              {currentSection === SECTIONS.overview && (
                <div style={styles.stack}>
                  <div style={styles.overviewHero}>
                    <div>
                      <div style={styles.panelTitle}>Round {selectedRound?.roundNumber || upcomingRound?.roundNumber || activeRoundNumber || '-'} Overview</div>
                      <div style={styles.panelMeta}>
                        {selectedRound
                          ? `${selectedRound.label || `Round ${selectedRound.roundNumber}`} is ${selectedRoundDisplayStatus}.`
                          : overviewHeadline}
                      </div>
                    </div>
                    <div style={styles.actions}>
                      {showRoundLiveNotice && (
                        <div style={{ ...styles.statusTextPill, ...styles.statusTextPillDone }}>
                          Round is live
                        </div>
                      )}
                      {canShowStartGameButton && (
                        <button type="button" onClick={handleStartGame} disabled={saving} style={styles.primaryBtn}>
                          {saving ? 'Starting...' : startGameLabel}
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={styles.overviewSummaryGrid}>
                    <div style={styles.overviewSummaryCard}>
                      <div style={styles.overviewSummaryLabel}>Players</div>
                      <div style={styles.overviewSummaryValue}>{selectedRoundPlayerCount || 0}</div>
                    </div>
                    <div style={styles.overviewSummaryCard}>
                      <div style={styles.overviewSummaryLabel}>Out</div>
                      <div style={styles.overviewSummaryValue}>{selectedRoundLoserIds.size}</div>
                    </div>
                    <div style={styles.overviewSummaryCard}>
                      <div style={styles.overviewSummaryLabel}>Winner + Bye</div>
                      <div style={styles.overviewSummaryValue}>{selectedRoundWinnerIds.size}</div>
                    </div>
                    <div style={styles.overviewSummaryCard}>
                      <div style={styles.overviewSummaryLabel}>Matches Done</div>
                      <div style={styles.overviewSummaryValue}>{selectedRoundCompletedCount}</div>
                    </div>
                  </div>
                  <div style={styles.compactPanel}>
                    <div style={styles.panelTitle}>Round Notes</div>
                    <div style={styles.panelMeta}>{overviewSubline}</div>
                    {selectedRound && (
                      <div style={styles.overviewList}>
                        <div style={styles.overviewListItem}><span style={styles.overviewListKey}>Status</span><span style={styles.overviewListValue}>{selectedRoundDisplayStatus}</span></div>
                        <div style={styles.overviewListItem}><span style={styles.overviewListKey}>Bye Players</span><span style={styles.overviewListValue}>{selectedRoundByeIds.length}</span></div>
                        <div style={styles.overviewListItem}><span style={styles.overviewListKey}>Registered</span><span style={styles.overviewListValue}>{selectedTournament.participantCount}</span></div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {currentSection === SECTIONS.players && (
                <div style={styles.stack}>
                  <div style={styles.panelHeader}>
                    <div>
                      <div style={styles.panelTitle}>Players</div>
                      {/* <div style={styles.panelMeta}>Only qualified players for round {selectedRound?.roundNumber || '-'} are shown here.</div> */}
                    </div>
                    <div style={styles.playerTools}>
                      <RefreshButton
                        onClick={() => refreshCurrentView('players')}
                        spinning={refreshingSection === 'players'}
                        label="Refresh players"
                      />
                      <div style={styles.playerFilterRow}>
                        {[
                          { id: 'all', label: 'All' },
                          { id: 'eliminated', label: 'Eliminated' },
                          { id: 'won', label: 'Won' },
                          { id: 'byes', label: 'Byes' },
                          { id: 'not_started', label: 'Not started' },
                        ].map((filter) => (
                          <button
                            key={filter.id}
                            type="button"
                            onClick={() => setPlayerFilter(filter.id)}
                            style={playerFilterChipStyle(filter.id, playerFilter === filter.id)}
                          >
                            {filter.label}
                          </button>
                        ))}
                      </div>
                      <input value={playerSearch} onChange={(e) => setPlayerSearch(e.target.value)} placeholder="Search name or agent" style={{ ...styles.input, width: 220, padding: '10px 12px' }} />
                    </div>
                  </div>
                  <div style={styles.tableWrap}>
                    <table style={styles.table}>
                      <thead><tr><th style={styles.th}>Player</th><th style={styles.th}>Agent</th><th style={styles.th}>Status</th><th style={styles.th}>Scoreline</th><th style={styles.th}>Played On</th></tr></thead>
                      <tbody>
                        {playersPage.items.map((participant) => (
                          <tr key={participant.id}>
                            <td style={styles.td}><div style={styles.primaryText}>{participant.name}</div><div style={styles.secondaryText}>@{participant.username}</div></td>
                            <td style={styles.td}>{participant.parentAgentUsername || 'Direct'}</td>
                            <td style={styles.td}>
                              <span style={badge(getParticipantRoundStatus(participant, selectedRound, selectedRoundWinnerIds, selectedRoundLoserIds, selectedRoundByeIds))}>
                                {getParticipantRoundStatus(participant, selectedRound, selectedRoundWinnerIds, selectedRoundLoserIds, selectedRoundByeIds)}
                              </span>
                            </td>
                            <td style={styles.td}>{getParticipantRoundScoreline(participant, selectedRound, selectedRoundByeIds)}</td>
                            <td style={styles.td}>{formatPlayedAt(getParticipantRoundMatch(selectedRound, participant.id)?.completedAt || getParticipantRoundMatch(selectedRound, participant.id)?.createdAt)}</td>
                          </tr>
                        ))}
                        {playersPage.items.length === 0 && (
                          <tr><td style={styles.td} colSpan={5}>No players found for this round.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <Pager page={playersPage.safePage} totalPages={playersPage.totalPages} onChange={setPlayerPage} />
                </div>
              )}

              {currentSection === SECTIONS.matches && (
                <div style={styles.stack}>
                  <div style={styles.panelHeader}>
                    <div>
                      <div style={styles.panelTitle}>Matches</div>
                      <div style={styles.panelMeta}>Completed matches for round {selectedRound?.roundNumber || '-'}.</div>
                    </div>
                    <RefreshButton
                      onClick={() => refreshCurrentView('matches')}
                      spinning={refreshingSection === 'matches'}
                      label="Refresh matches"
                    />
                  </div>
                  <div style={styles.roundTableSection}>
                    <div style={styles.roundTableHeader}>
                      <div>
                        <div style={styles.roundTableTitle}>{selectedRound?.label || `Round ${selectedRound?.roundNumber || '-'}`}</div>
                        <div style={styles.roundTableMeta}>
                          Players: {selectedRoundPlayerCount} | Completed: {selectedRoundCompletedCount} | Byes: {selectedRoundByeIds.length}
                        </div>
                      </div>
                      <span style={badge(selectedRoundDisplayStatus)}>{selectedRoundDisplayStatus}</span>
                    </div>
                    <div style={styles.tableWrap}>
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            <th style={styles.th}>Slot</th>
                            <th style={styles.th}>Players</th>
                            <th style={styles.th}>Score</th>
                            <th style={styles.th}>Winner</th>
                            <th style={styles.th}>Status</th>
                            <th style={styles.th}>Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedRoundCompletedMatchesPage.items.length === 0 && (
                            <tr><td style={styles.td} colSpan={6}>No completed matches yet for this round.</td></tr>
                          )}
                          {selectedRoundCompletedMatchesPage.items.map((match) => (
                            <tr key={match.id}>
                              <td style={styles.td}>{match.slot || '-'}</td>
                              <td style={styles.td}>
                                <div style={styles.primaryText}>{match.player1Name || 'TBD'} vs {match.player2Name || 'BYE'}</div>
                              </td>
                              <td style={styles.td}>{formatScoreline(match)}</td>
                              <td style={styles.td}>{match.winnerName || (match.isBye ? match.player1Name : '-')}</td>
                              <td style={styles.td}><span style={badge(match.status)}>{match.status}</span></td>
                              <td style={styles.td}>{formatPlayedAt(match.completedAt || match.createdAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <Pager page={selectedRoundCompletedMatchesPage.safePage} totalPages={selectedRoundCompletedMatchesPage.totalPages} onChange={setMatchPage} />
                </div>
              )}

              {currentSection === SECTIONS.subMatches && (
                <div style={styles.stack}>
                  <div style={styles.panelHeader}>
                    <div>
                      <div style={styles.panelTitle}>Sub Matches</div>
                      <div style={styles.panelMeta}>
                        {subMatchDescription} These matches go live automatically as soon as the current round is completed.
                      </div>
                    </div>
                    <div style={styles.headerActions}>
                      <RefreshButton
                        onClick={() => refreshCurrentView('subMatches')}
                        spinning={refreshingSection === 'subMatches'}
                        label="Refresh sub matches"
                      />
                      <div style={styles.secondaryText}>
                        {placementCurrentRound ? `${placementCurrentRound.label} is ${placementCurrentRound.status}` : `${placementParticipants.length} players`}
                      </div>
                    </div>
                  </div>
                  <div style={styles.compactPanel}>
                    <div style={styles.panelTitle}>Qualified Players</div>
                    <div style={styles.subMatchPlayerWrap}>
                      {placementParticipants.map((participant) => (
                        <div key={participant.id} style={styles.subMatchPlayerChip}>
                          <span style={styles.primaryText}>{participant.name}</span>
                          <span style={styles.secondaryText}>@{participant.username}</span>
                        </div>
                      ))}
                      {placementParticipants.length === 0 && <div style={styles.empty}>No sub match players yet.</div>}
                    </div>
                  </div>
                  <div style={styles.subRoundTables}>
                    {placementRoundsPage.items.length === 0 && (
                      <div style={styles.emptyCard}>Sub matches will appear here once the selected round is completed.</div>
                    )}
                    {placementRoundsPage.items.map((round) => (
                      <div key={round.id} style={styles.roundTableSection}>
                        <div style={styles.roundTableHeader}>
                          <div>
                            <div style={styles.roundTableTitle}>{round.label || `Sub Match ${round.roundNumber || '-'}`}</div>
                            <div style={styles.roundTableMeta}>
                              {round.rankRange ? `${round.rankRange} | ` : ''}{round.resolvedMatches.length} match{round.resolvedMatches.length === 1 ? '' : 'es'}
                            </div>
                          </div>
                          <span style={badge(round.status)}>{round.status || 'upcoming'}</span>
                        </div>
                        <div style={styles.tableWrap}>
                          <table style={styles.table}>
                            <thead>
                              <tr>
                                <th style={styles.th}>Slot</th>
                                <th style={styles.th}>Players</th>
                                <th style={styles.th}>Score</th>
                                <th style={styles.th}>Winner</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              {round.resolvedMatches.map((match) => (
                                <tr key={match.id}>
                                  <td style={styles.td}>{match.slot || '-'}</td>
                                  <td style={styles.td}>
                                    <div style={styles.primaryText}>{match.player1Name || 'TBD'} vs {match.player2Name || 'BYE'}</div>
                                  </td>
                                  <td style={styles.td}>{formatScoreline(match)}</td>
                                  <td style={styles.td}>{match.winnerName || (match.isBye ? match.player1Name : '-')}</td>
                                  <td style={styles.td}><span style={badge(match.status)}>{match.status}</span></td>
                                  <td style={styles.td}>{formatPlayedAt(match.completedAt || match.createdAt)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Pager page={placementRoundsPage.safePage} totalPages={placementRoundsPage.totalPages} onChange={setSubMatchPage} />
                </div>
              )}

            </section>
          </div>
        </div>
      )}

      {!isReadOnly && editorMode && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.panelHeader}>
              <div><div style={styles.panelTitle}>{editorMode === 'edit' ? 'Edit Tournament' : 'Create Tournament'}</div><div style={styles.panelMeta}>Compact form for tournament setup.</div></div>
              <button type="button" onClick={() => !saving && setEditorMode(null)} style={styles.subtleBtn}>Close</button>
            </div>
            <form onSubmit={editorMode === 'edit' ? handleSaveTournament : handleCreate} style={styles.formGrid}>
              <label style={styles.label}>Tournament Name<input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} style={styles.input} required /></label>
              <label style={styles.label}>Entry Fee<input type="number" min="0" value={form.entryFee} onChange={(e) => setForm((p) => ({ ...p, entryFee: e.target.value }))} style={styles.input} /></label>
              <label style={styles.label}>Max Players<input type="number" min="20" max="50" value={form.maxPlayers} onChange={(e) => setForm((p) => ({ ...p, maxPlayers: e.target.value }))} placeholder="Enter 20 to 50" style={styles.input} required disabled={editorMode === 'edit' ? !canEdit : false} /></label>
              <label style={{ ...styles.label, gridColumn: '1 / -1' }}>Description<textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} style={styles.textarea} rows={3} /></label>
              <button type="submit" disabled={saving} style={styles.primaryBtn}>{saving ? 'Saving...' : editorMode === 'edit' ? 'Save Tournament' : 'Create Tournament'}</button>
            </form>
          </div>
        </div>
      )}

      {showPrizeBoard && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, ...styles.prizeBoardModal }}>
            <div style={styles.panelHeader}>
              <div>
                <div style={styles.panelTitle}>Prize Leaderboard</div>
                <div style={styles.panelMeta}>Fixed tournament ranks 1 to 9. Player and agent appear when the rank is resolved.</div>
              </div>
              <div style={styles.headerActions}>
                <RefreshButton
                  onClick={() => refreshCurrentView('leaderboard')}
                  spinning={refreshingSection === 'leaderboard'}
                  label="Refresh leaderboard"
                />
                <button type="button" onClick={() => setShowPrizeBoard(false)} style={styles.subtleBtn}>Close</button>
              </div>
            </div>
            <div style={styles.prizeBoardTable}>
              <div style={{ ...styles.prizeBoardTableRow, ...styles.prizeBoardTableHead }}>
                <div>Rank</div>
                <div>Player / Agent</div>
                <div style={styles.prizeBoardTablePrizeHead}>Prize</div>
              </div>
              {tournamentPrizeBoard.map((entry) => (
                <div key={entry.rank} style={styles.prizeBoardTableRow}>
                  <div style={styles.prizeBoardRank}>#{entry.rank}</div>
                  <div style={styles.prizeBoardPlayer}>
                    <div style={styles.primaryText}>{entry.participant?.name || ''}</div>
                    <div style={styles.secondaryText}>
                      {entry.participant
                        ? `@${entry.participant.username}${entry.participant.parentAgentUsername ? ` • Agent: ${entry.participant.parentAgentUsername}` : ''}`
                        : ''}
                    </div>
                  </div>
                  <div style={styles.prizeBoardCoins}>{Number(entry.coins || 0).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { display: 'grid', gap: 12 },
  topBar: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 10, alignItems: 'start' },
  kicker: { color: '#b8623f', textTransform: 'uppercase', letterSpacing: 1.3, fontSize: 12, fontWeight: 800 },
  topTitle: { color: '#241816', fontSize: 16, fontWeight: 800, marginTop: 5 },
  topStats: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 },
  metric: { padding: 10, borderRadius: 14, background: 'rgba(255,251,247,0.96)', border: '1px solid rgba(166,106,75,0.12)' },
  metricValue: { color: '#241816', fontSize: 16, fontWeight: 800, lineHeight: 1.3, wordBreak: 'break-word' },
  metricLabel: { color: '#7b665c', fontSize: 11, marginTop: 4 },
  notice: { borderRadius: 12, padding: '10px 12px', fontWeight: 700, fontSize: 13 },
  noticeOk: { background: 'rgba(255,251,247,0.92)', border: '1px solid rgba(166,106,75,0.12)', color: '#8c5a3c' },
  noticeError: { background: 'rgba(255,243,239,0.95)', border: '1px solid rgba(201,91,70,0.16)', color: '#c95b46' },
  panel: { background: 'rgba(255,251,247,0.92)', border: '1px solid rgba(166,106,75,0.12)', borderRadius: 18, padding: 14, display: 'grid', gap: 12 },
  compactPanel: { background: 'rgba(248,242,237,0.94)', border: '1px solid rgba(166,106,75,0.10)', borderRadius: 16, padding: 12, display: 'grid', gap: 8 },
  panelHeader: { display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' },
  headerActions: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  panelTitle: { color: '#211715', fontSize: 16, fontWeight: 800 },
  panelMeta: { color: '#7b665c', fontSize: 12, lineHeight: 1.45, marginTop: 3 },
  primaryBtn: { border: '1px solid rgba(184,105,67,0.26)', background: 'linear-gradient(145deg, #d68860, #b46642)', color: '#fffaf7', borderRadius: 12, padding: '10px 14px', cursor: 'pointer', fontWeight: 800, fontSize: 13 },
  subtleBtn: { border: '1px solid rgba(166,106,75,0.14)', background: '#fff8f3', color: '#372620', borderRadius: 10, padding: '8px 12px', cursor: 'pointer', fontWeight: 700, fontSize: 12 },
  refreshBtn: { width: 32, height: 32, display: 'inline-grid', placeItems: 'center', borderRadius: 999, border: '1px solid rgba(166,106,75,0.16)', background: '#fffaf7', color: '#8c5a3c', cursor: 'pointer', padding: 0, flexShrink: 0, boxShadow: '0 6px 16px rgba(80,45,31,0.06)', overflow: 'hidden' },
  refreshBtnSpinning: { opacity: 0.8, cursor: 'default' },
  statusTextPill: { borderRadius: 12, padding: '10px 14px', fontWeight: 800, fontSize: 13, border: '1px solid rgba(166,106,75,0.12)', background: '#fff8f3' },
  statusTextPillDone: { color: '#2f6a42' },
  listRow: { border: '1px solid rgba(166,106,75,0.12)', background: 'rgba(250,245,240,0.92)', borderRadius: 14, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, textAlign: 'left', cursor: 'pointer' },
  listTitle: { color: '#241816', fontSize: 14, fontWeight: 800 },
  listMeta: { color: '#8b776d', fontSize: 12, marginTop: 4, lineHeight: 1.35 },
  detailLoaderWrap: { minHeight: 'calc(100vh - 180px)', display: 'grid', alignItems: 'start' },
  detailLoaderCard: { background: 'rgba(255,251,247,0.96)', border: '1px solid rgba(166,106,75,0.12)', borderRadius: 18, padding: '22px 18px', display: 'grid', justifyItems: 'center', alignContent: 'center', gap: 10, minHeight: 180, textAlign: 'center', boxShadow: '0 10px 24px rgba(80,45,31,0.05)' },
  detailLoaderSpinner: { width: 44, height: 44, display: 'grid', placeItems: 'center', borderRadius: 999, background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95), rgba(242,227,215,0.9))', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.85)' },
  detailPage: { display: 'grid', gap: 8 },
  detailHeader: { display: 'grid', gap: 6 },
  detailHeaderTop: { display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' },
  detailHeaderMain: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', minWidth: 0 },
  detailName: { color: '#211715', fontSize: 24, fontWeight: 800, lineHeight: 1.2 },
  detailMetaInline: { color: '#6f5b52', fontSize: 13, fontWeight: 600, lineHeight: 1.4 },
  detailPipe: { color: '#c5a58f', fontSize: 12, fontWeight: 700 },
  detailStack: { display: 'grid', gap: 10, marginTop: -2 },
  progressPanel: { background: 'rgba(255,251,247,0.96)', border: '1px solid rgba(166,106,75,0.12)', borderRadius: 18, padding: 12, display: 'grid', gap: 10 },
  progressMetaRow: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
  progressMetaPill: { display: 'inline-flex', alignItems: 'center', borderRadius: 999, padding: '6px 10px', background: 'rgba(184,105,67,0.08)', color: '#7d563f', fontSize: 12, fontWeight: 700 },
  progressTrack: { position: 'relative', height: 10, borderRadius: 999, background: 'rgba(214,182,164,0.32)', overflow: 'hidden' },
  progressFill: { position: 'absolute', inset: 0, width: '0%', borderRadius: 999, background: 'linear-gradient(90deg, #d68860 0%, #b46642 100%)' },
  progressStepRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(92px, 1fr))', gap: 6 },
  progressStep: { border: '1px solid rgba(166,106,75,0.12)', background: '#fff8f3', color: '#4d3d37', borderRadius: 12, padding: '8px 8px', display: 'grid', gap: 2, textAlign: 'center', cursor: 'pointer', fontWeight: 700 },
  progressStepDone: { background: '#fff', color: '#8c5a3c', borderColor: 'rgba(166,106,75,0.18)' },
  progressStepCurrent: { background: 'linear-gradient(145deg, #f3c29f, #d68860)', color: '#ffffff', borderColor: 'rgba(184,105,67,0.55)', boxShadow: '0 0 0 2px rgba(214,136,96,0.18)' },
  progressStepFuture: { opacity: 0.7 },
  progressStepNumber: { fontSize: 18, fontWeight: 800, lineHeight: 1 },
  progressStepLabel: { fontSize: 11, fontWeight: 700 },
  topTabs: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  topTabBtn: { border: '1px solid rgba(166,106,75,0.12)', background: '#fff8f3', color: '#4d3d37', borderRadius: 12, padding: '10px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 13 },
  topTabBtnActive: { background: 'linear-gradient(145deg, #d68860, #b46642)', color: '#fffaf7' },
  prizeBoardTable: { display: 'grid', gap: 6, maxHeight: 'min(62vh, 520px)', overflowY: 'auto', paddingRight: 4 },
  prizeBoardTableRow: { display: 'grid', gridTemplateColumns: '56px minmax(0, 1fr) 94px', gap: 10, alignItems: 'center', borderRadius: 10, border: '1px solid rgba(166,106,75,0.10)', background: '#fffaf7', padding: '8px 10px' },
  prizeBoardTableHead: { background: 'rgba(248,242,237,0.94)', color: '#7b665c', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.7, position: 'sticky', top: 0, zIndex: 1 },
  prizeBoardRank: { color: '#8c5a3c', fontSize: 15, fontWeight: 800, lineHeight: 1, textAlign: 'center' },
  prizeBoardPlayer: { minWidth: 0 },
  prizeBoardCoins: { color: '#2f6a42', fontSize: 13, fontWeight: 800, textAlign: 'right' },
  prizeBoardTablePrizeHead: { textAlign: 'right' },
  contentWide: { background: 'rgba(255,251,247,0.96)', border: '1px solid rgba(166,106,75,0.12)', borderRadius: 18, padding: 14, minHeight: 'calc(100vh - 240px)', maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' },
  stack: { display: 'grid', gap: 12 },
  playerTools: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' },
  playerFilterRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  filterChip: { border: '1px solid rgba(166,106,75,0.12)', background: '#fff8f3', color: '#4d3d37', borderRadius: 999, padding: '8px 12px', cursor: 'pointer', fontWeight: 700, fontSize: 12, transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease' },
  filterChipActive: { boxShadow: '0 0 0 2px rgba(90,67,54,0.08)', transform: 'translateY(-1px)' },
  overviewHero: { background: 'rgba(248,242,237,0.94)', border: '1px solid rgba(166,106,75,0.10)', borderRadius: 16, padding: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  overviewSummaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 },
  overviewSummaryCard: { background: '#fffaf7', border: '1px solid rgba(166,106,75,0.10)', borderRadius: 14, padding: 12, display: 'grid', gap: 5 },
  overviewSummaryLabel: { color: '#8b776d', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700 },
  overviewSummaryValue: { color: '#241816', fontSize: 22, fontWeight: 800, lineHeight: 1.15 },
  overviewList: { display: 'grid', gap: 6 },
  overviewListItem: { color: '#5f4d46', fontSize: 13, lineHeight: 1.45, display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' },
  overviewListKey: { color: '#7b665c', fontWeight: 700 },
  overviewListValue: { color: '#241816', fontWeight: 800 },
  subMatchPlayerWrap: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  subMatchPlayerChip: { border: '1px solid rgba(166,106,75,0.12)', borderRadius: 12, padding: '9px 11px', background: '#fffaf7', display: 'grid', gap: 2 },
  roundTableSection: { display: 'grid', gap: 8, borderRadius: 14, padding: 10, background: '#fffdfb', border: '1px solid rgba(166,106,75,0.1)' },
  roundTableHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' },
  roundTableTitle: { color: '#241816', fontSize: 14, fontWeight: 800, lineHeight: 1.2 },
  roundTableMeta: { color: '#7b665c', fontSize: 11, marginTop: 2, lineHeight: 1.35 },
  subRoundTables: { display: 'grid', gap: 10 },
  emptyCard: { color: '#7a655c', padding: 12, fontSize: 12, borderRadius: 12, background: '#fffaf7', border: '1px dashed rgba(166,106,75,0.18)' },
  tableWrap: { overflowX: 'auto', border: '1px solid rgba(166,106,75,0.12)', borderRadius: 12, background: '#fffaf7' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 700 },
  th: { textAlign: 'left', padding: '12px 14px', color: '#7b665c', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.9, borderBottom: '1px solid rgba(166,106,75,0.12)' },
  td: { padding: '12px 14px', color: '#2a1c18', borderBottom: '1px solid rgba(166,106,75,0.08)', verticalAlign: 'top', fontSize: 13 },
  tr: { cursor: 'pointer' },
  primaryText: { color: '#241816', fontWeight: 700 },
  secondaryText: { color: '#8b776d', fontSize: 11, marginTop: 3 },
  badge: { borderRadius: 999, padding: '5px 9px', fontSize: 11, fontWeight: 800, textTransform: 'capitalize', border: '1px solid transparent' },
  badgeDone: { background: 'rgba(98,161,115,0.16)', color: '#2f6a42', borderColor: 'rgba(98,161,115,0.18)' },
  badgeWon: { background: '#eef8f0', color: '#2f6a42', borderColor: 'rgba(98,161,115,0.28)' },
  badgeBye: { background: '#eef6ff', color: '#3d629f', borderColor: 'rgba(79,120,199,0.28)' },
  badgeLive: { background: 'rgba(79,120,199,0.12)', color: '#355799', borderColor: 'rgba(79,120,199,0.18)' },
  badgeDanger: { background: 'rgba(199,95,95,0.12)', color: '#994949', borderColor: 'rgba(199,95,95,0.16)' },
  badgeNotStarted: { background: '#fff8d9', color: '#8a6a12', borderColor: 'rgba(194,155,49,0.32)' },
  badgeDefault: { background: 'rgba(184,105,67,0.08)', color: '#7d563f', borderColor: 'rgba(184,105,67,0.16)' },
  pager: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  pagerText: { color: '#6f5b52', fontWeight: 700, fontSize: 12 },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, alignItems: 'end' },
  label: { display: 'grid', gap: 7, color: '#54433c', fontWeight: 600, fontSize: 13 },
  input: { border: '1px solid rgba(166,106,75,0.14)', background: 'rgba(247,241,235,0.94)', color: '#251916', borderRadius: 10, padding: '11px 12px', outline: 'none', width: '100%' },
  textarea: { border: '1px solid rgba(166,106,75,0.14)', background: 'rgba(247,241,235,0.94)', color: '#251916', borderRadius: 10, padding: '11px 12px', outline: 'none', width: '100%', resize: 'vertical' },
  empty: { color: '#7a655c', padding: 12, fontSize: 13 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(22,12,8,0.32)', display: 'grid', placeItems: 'center', padding: 20, zIndex: 2000 },
  modal: { width: 'min(920px, 100%)', background: 'rgba(255,251,247,0.98)', border: '1px solid rgba(166,106,75,0.12)', borderRadius: 18, padding: 16, display: 'grid', gap: 14, boxShadow: '0 24px 60px rgba(80,45,31,0.2)' },
  prizeBoardModal: { width: 'min(620px, 100%)', maxHeight: 'min(76vh, 680px)', padding: 12, gap: 10 },
};
